import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Search, Package, User, Clock, CreditCard, MapPin, Phone, Mail, FileText, TrendingUp, Filter } from "lucide-react";
import axiosInstance from '../axiosInstance';

function DailySales() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [orderSource, setOrderSource] = useState("Overall"); // Overall, POS, Website
  const [paymentStatus, setPaymentStatus] = useState("All"); // All, Paid, Unpaid

  const fetchOrdersByDate = async () => {
    if (!selectedDate) {
      setError("Please select a date");
      return;
    }

    setLoading(true);
    setError("");
    setHasSearched(true);

    try {
      const response = await axiosInstance.get(`/orders/orders-by-date`, {
        params: { date: selectedDate }
      });
      console.log(response.data);
      setOrders(response.data);
    } catch (err) {
      console.error("Error fetching orders:", err);
      if (err.response && err.response.status === 404) {
        setError("No orders found for this date");
      } else {
        setError("Failed to fetch orders. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getFilteredOrders = () => {
    let filtered = [...orders];
    console.log("FILTERED DATA",filtered)
    // Filter by order source
    if (orderSource === "POS") {
      filtered = filtered.filter(order => order.order_source === "EPOS");
    } else if (orderSource === "Website") {
      filtered = filtered.filter(order => order.order_source === "Website");
    }

    // Filter by payment status
    if (paymentStatus === "Paid") {
      filtered = filtered.filter(order => order.payment_type && order.payment_type.trim() !== "");
    } else if (paymentStatus === "Unpaid") {
      filtered = filtered.filter(order => !order.payment_type || order.payment_type.trim() === "");
    }

    return filtered;
  };

  const filteredOrders = getFilteredOrders();

  const calculateTotalSales = () => {
    return filteredOrders.reduce((sum, order) => sum + parseFloat(order.total_price || 0), 0);
  };

  const calculateTotalDiscount = () => {
    return filteredOrders.reduce((sum, order) => sum + parseFloat(order.discount || 0), 0);
  };

  const getStatusColor = (status) => {
    const statusColors = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      confirmed: "bg-blue-100 text-blue-800 border-blue-200",
      preparing: "bg-purple-100 text-purple-800 border-purple-200",
      ready: "bg-cyan-100 text-cyan-800 border-cyan-200",
      "out-for-delivery": "bg-orange-100 text-orange-800 border-orange-200",
      delivered: "bg-green-100 text-green-800 border-green-200",
      cancelled: "bg-red-100 text-red-800 border-red-200",
      completed: "bg-emerald-100 text-emerald-800 border-emerald-200"
    };
    return statusColors[status?.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 px-4 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/admin/reports")}
            className="group flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-all mb-6 font-medium"
            style={{ fontFamily: "Bambino" }}
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>Back to Reports</span>
          </button>

          <div className="flex items-center gap-3 mb-4">
            <Calendar className="w-10 h-10 text-green-600" />
            <h1
              className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 via-emerald-500 to-teal-500 bg-clip-text text-transparent"
              style={{ fontFamily: "Bambino", lineHeight: "1.2" }}
            >
              Daily Sales Report
            </h1>
          </div>

          <p
            className="text-gray-600 text-base"
            style={{ fontFamily: "Bambino" }}
          >
            View all orders and details for a specific date
          </p>
        </div>

        {/* Date Selection Card */}
        <div className="bg-white/70 backdrop-blur-md rounded-3xl p-8 shadow-xl border-2 border-green-200 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <label
                className="block text-gray-700 font-bold mb-2 text-sm"
                style={{ fontFamily: "Bambino" }}
              >
                Select Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={new Date().toISOString().split("T")[0]}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500 transition-all"
                style={{ fontFamily: "Bambino" }}
              />
            </div>

            <button
              onClick={fetchOrdersByDate}
              disabled={loading || !selectedDate}
              className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-3 rounded-xl font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              style={{ fontFamily: "Bambino" }}
            >
              <Search className="w-5 h-5" />
              {loading ? "Loading..." : "Search Orders"}
            </button>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-700 text-sm" style={{ fontFamily: "Bambino" }}>
              {error}
            </div>
          )}
        </div>

        {/* Filter Options */}
        {hasSearched && orders.length > 0 && (
          <div className="bg-white/70 backdrop-blur-md rounded-3xl p-6 shadow-xl border-2 border-blue-200 mb-8">
            <div className="flex items-center gap-2 mb-6">
              <Filter className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-800" style={{ fontFamily: "Bambino" }}>
                Filter Orders
              </h2>
            </div>

            {/* Order Source Filter */}
            <div className="mb-6">
              <label className="block text-gray-700 font-bold mb-3 text-sm" style={{ fontFamily: "Bambino" }}>
                Order Source
              </label>
              <div className="flex flex-wrap gap-3">
                {["Overall", "POS", "Website"].map((source) => (
                  <button
                    key={source}
                    onClick={() => setOrderSource(source)}
                    className={`px-6 py-3 rounded-xl font-bold transition-all ${
                      orderSource === source
                        ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg scale-105"
                        : "bg-white border-2 border-gray-300 text-gray-700 hover:border-blue-400"
                    }`}
                    style={{ fontFamily: "Bambino" }}
                  >
                    {source}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Status Filter */}
            <div>
              <label className="block text-gray-700 font-bold mb-3 text-sm" style={{ fontFamily: "Bambino" }}>
                Payment Status
              </label>
              <div className="flex flex-wrap gap-3">
                {["All", "Paid", "Unpaid"].map((status) => (
                  <button
                    key={status}
                    onClick={() => setPaymentStatus(status)}
                    className={`px-6 py-3 rounded-xl font-bold transition-all ${
                      paymentStatus === status
                        ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg scale-105"
                        : "bg-white border-2 border-gray-300 text-gray-700 hover:border-purple-400"
                    }`}
                    style={{ fontFamily: "Bambino" }}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {hasSearched && orders.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/70 backdrop-blur-md rounded-2xl p-6 shadow-lg border-2 border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium" style={{ fontFamily: "Bambino" }}>Total Orders</p>
                  <p className="text-3xl font-bold text-purple-600 mt-1" style={{ fontFamily: "Bambino" }}>{filteredOrders.length}</p>
                </div>
                <Package className="w-12 h-12 text-purple-600 opacity-20" />
              </div>
            </div>

            <div className="bg-white/70 backdrop-blur-md rounded-2xl p-6 shadow-lg border-2 border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium" style={{ fontFamily: "Bambino" }}>Total Sales</p>
                  <p className="text-3xl font-bold text-green-600 mt-1" style={{ fontFamily: "Bambino" }}>£{calculateTotalSales().toFixed(2)}</p>
                </div>
                <TrendingUp className="w-12 h-12 text-green-600 opacity-20" />
              </div>
            </div>

            <div className="bg-white/70 backdrop-blur-md rounded-2xl p-6 shadow-lg border-2 border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium" style={{ fontFamily: "Bambino" }}>Total Discount</p>
                  <p className="text-3xl font-bold text-orange-600 mt-1" style={{ fontFamily: "Bambino" }}>£{calculateTotalDiscount().toFixed(2)}</p>
                </div>
                <CreditCard className="w-12 h-12 text-orange-600 opacity-20" />
              </div>
            </div>
          </div>
        )}

        {/* No Results Message */}
        {hasSearched && filteredOrders.length === 0 && !loading && (
          <div className="bg-white/70 backdrop-blur-md rounded-3xl p-12 shadow-xl border-2 border-gray-200 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-700 mb-2" style={{ fontFamily: "Bambino" }}>
              No Orders Found
            </h3>
            <p className="text-gray-600" style={{ fontFamily: "Bambino" }}>
              {orders.length > 0 
                ? "No orders match the selected filters."
                : "There are no orders for the selected date."}
            </p>
          </div>
        )}

        {/* Orders List */}
        {filteredOrders.length > 0 && (
          <div className="space-y-6">
            {filteredOrders.map((order) => (
              <div
                key={order.order_id}
                className="bg-white/70 backdrop-blur-md rounded-3xl p-6 md:p-8 shadow-xl border-2 border-gray-200 hover:shadow-2xl transition-all"
              >
                {/* Order Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 pb-6 border-b-2 border-gray-200">
                  <div className="mb-4 md:mb-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-2xl font-bold text-gray-800" style={{ fontFamily: "Bambino" }}>
                        Order #{order.order_number || order.order_id}
                      </h3>
                      <span className={`px-3 py-1 rounded-lg text-xs font-bold border-2 ${getStatusColor(order.status)}`} style={{ fontFamily: "Bambino" }}>
                        {order.status?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600" style={{ fontFamily: "Bambino" }}>
                      <Clock className="w-4 h-4" />
                      {formatDateTime(order.created_at)}
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-sm text-gray-600 mb-1" style={{ fontFamily: "Bambino" }}>Total Amount</p>
                    <p className="text-3xl font-bold text-green-600" style={{ fontFamily: "Bambino" }}>
                      £{parseFloat(order.total_price).toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Order Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Customer Information */}
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-5 border-2 border-blue-200">
                    <div className="flex items-center gap-2 mb-4">
                      <User className="w-5 h-5 text-blue-600" />
                      <h4 className="font-bold text-gray-800" style={{ fontFamily: "Bambino" }}>Customer Details</h4>
                    </div>
                    <div className="space-y-2 text-sm" style={{ fontFamily: "Bambino" }}>
                      <p className="text-gray-700"><strong>Name:</strong> {order.customer?.name || "N/A"}</p>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Phone className="w-4 h-4" />
                        <span>{order.customer?.phone_number || "N/A"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Mail className="w-4 h-4" />
                        <span>{order.customer?.email || "N/A"}</span>
                      </div>
                      <div className="flex items-start gap-2 text-gray-700">
                        <MapPin className="w-4 h-4 mt-1" />
                        <span>
                          {order.customer?.street_address && (
                            <>
                              {order.customer.street_address}, {order.customer.city}, {order.customer.postal_code}
                            </>
                          ) || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Payment & Order Info */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-5 border-2 border-purple-200">
                    <div className="flex items-center gap-2 mb-4">
                      <CreditCard className="w-5 h-5 text-purple-600" />
                      <h4 className="font-bold text-gray-800" style={{ fontFamily: "Bambino" }}>Payment & Order Info</h4>
                    </div>
                    <div className="space-y-2 text-sm" style={{ fontFamily: "Bambino" }}>
                      <p className="text-gray-700"><strong>Payment Type:</strong> {order.payment_type?.toUpperCase() || "N/A"}</p>
                      <p className="text-gray-700"><strong>Order Type:</strong> {order.order_type?.toUpperCase() || "N/A"}</p>
                      <p className="text-gray-700"><strong>Order Source:</strong> {order.order_source?.toUpperCase() || "N/A"}</p>
                      <p className="text-gray-700"><strong>Transaction ID:</strong> {order.transaction_id || "N/A"}</p>
                      {order.discount > 0 && (
                        <p className="text-orange-600 font-bold"><strong>Discount:</strong> £{parseFloat(order.discount).toFixed(2)}</p>
                      )}
                      {order.change_due && (
                        <p className="text-gray-700"><strong>Change Due:</strong> £{parseFloat(order.change_due).toFixed(2)}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                {order.order_items && order.order_items.length > 0 && (
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-5 border-2 border-green-200">
                    <div className="flex items-center gap-2 mb-4">
                      <Package className="w-5 h-5 text-green-600" />
                      <h4 className="font-bold text-gray-800" style={{ fontFamily: "Bambino" }}>Order Items ({order.order_items.length})</h4>
                    </div>
                    <div className="space-y-3">
                      {order.order_items.map((item, index) => (
                        <div key={item.order_item_id || index} className="bg-white/70 rounded-xl p-4 border border-green-200">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-bold text-gray-800" style={{ fontFamily: "Bambino" }}>{item.item_name}</p>
                              <p className="text-sm text-gray-600" style={{ fontFamily: "Bambino" }}>
                                Quantity: {item.quantity} × {item.item_name}
                              </p>
                              {item.extras && item.extras.length > 0 && (
                                <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: "Bambino" }}>
                                  Extras: {item.extras.join(", ")}
                                </p>
                              )}
                            </div>
                            <p className="font-bold text-green-600 text-lg" style={{ fontFamily: "Bambino" }}>
                              £{item.total_price}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Extra Notes */}
                {order.extra_notes && (
                  <div className="mt-6 bg-yellow-50 rounded-2xl p-5 border-2 border-yellow-200">
                    <div className="flex items-start gap-2">
                      <FileText className="w-5 h-5 text-yellow-600 mt-1" />
                      <div>
                        <h4 className="font-bold text-gray-800 mb-1" style={{ fontFamily: "Bambino" }}>Notes</h4>
                        <p className="text-sm text-gray-700" style={{ fontFamily: "Bambino" }}>{order.extra_notes}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DailySales;